/**
 * Hero Validation & Protection System
 * يمنع إنشاء أكثر من Hero واحد ويوفر تحذيرات أمان
 */

import { HomeSection } from '@/hooks/useHomeSections';

export interface HeroValidationResult {
  isValid: boolean;
  warnings: string[];
  errors: string[];
  heroCount: number;
  heroSections: HomeSection[];
}

/**
 * فحص شامل للـ Hero sections
 */
export function validateHeroSections(sections: HomeSection[]): HeroValidationResult {
  const heroSections = sections.filter(s => 
    s.section_key === 'hero' && s.is_active !== false
  );
  
  const warnings: string[] = [];
  const errors: string[] = [];
  
  // فحص تعدد الـ Hero
  if (heroSections.length > 1) {
    errors.push(`تم اكتشاف ${heroSections.length} أقسام Hero. يُسمح بواحد فقط.`);
    warnings.push('Hero مكرر يؤثر على SEO وتجربة المستخدم.');
  }
  
  // فحص غياب الـ Hero
  if (heroSections.length === 0) {
    warnings.push('لا يوجد قسم Hero. يُنصح بإضافة قسم Hero للصفحة الرئيسية.');
  }
  
  // فحص محتوى الـ Hero
  heroSections.forEach((hero, index) => {
    if (!hero.title_ar && !hero.title_en) {
      warnings.push(`Hero ${index + 1}: لا يوجد عنوان رئيسي.`);
    }
    
    if (hero.sort_order !== 0) {
      warnings.push(`Hero ${index + 1}: يُفضل أن يكون الترتيب 0 (أول قسم).`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    warnings,
    errors,
    heroCount: heroSections.length,
    heroSections
  };
}

/**
 * منع إنشاء Hero مكرر قبل الحفظ
 */
export function preventDuplicateHero(
  existingSections: HomeSection[], 
  newSection: Partial<HomeSection>
): { allowed: boolean; reason?: string } {
  
  // إذا لم يكن القسم الجديد Hero، فلا مشكلة
  if (newSection.section_key !== 'hero') {
    return { allowed: true };
  }
  
  // عدّ الـ Hero الموجود
  const activeHeroCount = existingSections.filter(s => 
    s.section_key === 'hero' && s.is_active !== false && s.id !== newSection.id
  ).length;
  
  if (activeHeroCount >= 1) {
    return { 
      allowed: false, 
      reason: `يوجد بالفعل قسم Hero نشط. لا يمكن إضافة Hero آخر.`
    };
  }
  
  return { allowed: true };
}

/**
 * إصلاح تلقائي لمشكلة الـ Hero المكرر
 */
export function autoFixDuplicateHero(sections: HomeSection[]): {
  fixedSections: HomeSection[];
  changes: string[];
} {
  const heroSections = sections.filter(s => s.section_key === 'hero' && s.is_active !== false);
  const changes: string[] = [];
  
  if (heroSections.length <= 1) {
    return { fixedSections: sections, changes };
  }
  
  // إبقاء الـ Hero الأول (أقل sort_order) وتعطيل الباقي
  const sortedHeros = [...heroSections].sort((a, b) => a.sort_order - b.sort_order);
  const primaryHero = sortedHeros[0];
  const duplicateHeros = sortedHeros.slice(1);
  
  const fixedSections = sections.map(section => {
    if (duplicateHeros.some(h => h.id === section.id)) {
      changes.push(`تم تعطيل Hero مكرر: ${section.title_ar || section.title_en || section.id}`);
      return { ...section, is_active: false };
    }
    return section;
  });
  
  return { fixedSections, changes };
}